<template>
    <div>
        <v-col>
            <div class="mb-2">How are oversized items treated?</div>
            <v-radio-group v-model="overSizedItemPackingMethod" class="mt-0">
                <v-radio
                    label="Pack individually"
                    value="pack-individually"
                ></v-radio>
                <v-radio
                    label="Largest Package By Itself"
                    value="largest-package-by-itself"
                ></v-radio>
            </v-radio-group>
            <div class="text-caption">Note: If you'd like additional rules for larger items, add another packing rule prior to this one targeting those items by size, weight or any other parameter.</div>
        </v-col>

        <div class="d-flex flex-row align-center">
            <v-card-subtitle class="mr-auto">Manage Packages</v-card-subtitle>
            <CsvEditor :boxSettings="boxes" :boxesFields="boxesFields" @updatePackages="updatePackages" :validationRules="getValidationRules()"/>
        </div>


        <v-virtual-scroll
            ref="virtualScroll"
            :items="boxes"
            :item-height="boxVirtualHeight"
            :height="boxesVirtualHeight"
            >

            <template v-slot:default="{ item: box, index }">
                <div class="mb-2 grey lighten-5 pb-2 d-flex flex-row">
                    <v-row class="ma-0 w-100">
                        <v-col cols="8">
                            <v-row class="ma-0">
                                <v-col cols="4" class="pa-1">
                                    <v-text-field
                                        v-model="box.name"
                                        label="Name"
                                        :error-messages="getErrorMessage(index, 'name')"
                                        @blur="touchField(index, 'name')"
                                        >
                                    </v-text-field>
                                </v-col>

                                <v-col cols="4" class="pa-1">
                                    <v-text-field
                                        v-model="box.sku"
                                        label="SKU"
                                        >
                                    </v-text-field>
                                </v-col>

                                <v-col cols="4" class="pa-1">
                                    <v-select
                                    v-model="box.item_type"
                                    :items="itemTypes"
                                    label="Item Type"
                                    ></v-select>
                                </v-col>

                                <v-col cols="4" class="pa-1">
                                    <v-text-field
                                        v-model="box.length"
                                        type="number"
                                        label="Length (cm)"
                                        :error-messages="getErrorMessage(index, 'length')"
                                        @blur="touchField(index, 'length')"
                                        >
                                    </v-text-field>
                                </v-col>
                                <v-col cols="4" class="pa-1">
                                    <v-text-field
                                        v-model="box.width"
                                        type="number"
                                        label="Width (cm)"
                                        :error-messages="getErrorMessage(index, 'width')"
                                        @blur="touchField(index, 'width')"
                                        >
                                    </v-text-field>
                                </v-col>
                                <v-col cols="4" class="pa-1">
                                    <v-text-field
                                        v-model="box.height"
                                        type="number"
                                        label="Height (cm)"
                                        :error-messages="getErrorMessage(index, 'height')"
                                        @blur="touchField(index, 'height')"
                                        >
                                    </v-text-field>
                                </v-col>
                            </v-row>
                        </v-col>

                        <v-col cols="12" md="4">
                            <v-row class="ma-0">
                                <v-col cols="6" class="pa-1">
                                    <v-text-field
                                        v-model="box.weight"
                                        type="number"
                                        label="Item Weight (kg)"
                                        >
                                    </v-text-field>
                                </v-col>
                                <v-col cols="6" class="pa-1">
                                    <v-text-field
                                        v-model="box.weight_limit"
                                        type="number"
                                        label="Weight Limit (kg)"
                                        >
                                    </v-text-field>
                                </v-col>
                            </v-row>


                            <v-row class="ma-0">
                                <v-col cols="6" class="pa-1">
                                    <v-text-field
                                        v-model="box.quantity"
                                        label="Quantity"
                                        type="number"
                                        :error-messages="getErrorMessage(index, 'quantity')"
                                        @blur="touchField(index, 'quantity')"
                                        >
                                    </v-text-field>
                                </v-col>
                            </v-row>
                        </v-col>
                    </v-row>

                    <div class="d-flex justify-end p-3">
                        <span v-if="boxes.length > 1" class="ic-remove clickable" @click="onRemove(index)">
                            <i class="fa fa-times-circle fa-2x"></i>
                        </span>
                    </div>
                </div>
            </template>
        </v-virtual-scroll>

        <div class="d-flex justify-end">
            <v-btn small @click="onAdd">Add more package</v-btn>
        </div>

    </div>
</template>

<script>
import { required, integer } from 'vuelidate/lib/validators'
import ValidationHelper, {numeric} from '../PackingRuleValidationHelper'
import ManagePackagesHelper from '../ManagePackagesHelper'
import CsvEditor from './CsvEditor.vue'
export default {
    mixins: [ValidationHelper, ManagePackagesHelper],
    components: {
        CsvEditor
    },
    props: {
        itemTypes: Array,
        formData: Object
    },
    data() {
        return {
            boxes: this.formData.packages,
            boxesFields: [
                'name',
                'sku',
                'item_type',
                'length',
                'width',
                'height',
                'weight',
                'weight_limit',
                'quantity'
            ],
            boxVirtualHeight: 200
        }
    },

    validations: {
        boxes: {
            $each: {
                name: { required },
                length: { required, numeric },
                width: { required, numeric },
                height: { required, numeric },
                quantity: { required, integer },
                weight: { numeric },
                weight_limit: { numeric }
            }
        }
    },

    computed: {
        overSizedItemPackingMethod: {
            get() {
                if (this.formData.options === null || this.formData.options == undefined || this.formData.options.length <= 0) {
                    this.formData.options = {} // important: Initialize options object
                }

            if(this.formData.options.overSizedItemPackingMethod === null || this.formData.options.overSizedItemPackingMethod == undefined) {
                    this.formData.options.overSizedItemPackingMethod = 'pack-individually'
                }

                return this.formData.options.overSizedItemPackingMethod;
            },
            set(value) {
                this.formData.options.overSizedItemPackingMethod = value;
            }
        },
        boxesVirtualHeight() {
            return this.boxes.length > 50 ? 1000 : this.boxes.length * this.boxVirtualHeight;
        }
    },

    mounted() {
        if(this.boxes.length == 0) {
            this.onAdd()
        }
    }
}
</script>